import axios from 'axios';
import router from '@/router';

class ApiRequest {
    get REQUEST_GET() { return 'get'; }
    get REQUEST_DOWNLOAD() { return 'download'; }
    get REQUEST_POST() { return 'post'; }
    get REQUEST_FILEUPLOAD() { return 'fileupload'; }
    get REQUEST_PATCH() { return 'patch'; }
    get REQUEST_PUT() { return 'put'; }
    get REQUEST_DELETE() { return 'delete'; }

    controllers = new Map();

    _createSignal(key, cancelPrevious = false) {
        if (cancelPrevious && this.controllers.has(key)) {
            this.controllers.get(key).abort();
        }
        const controller = new AbortController();
        this.controllers.set(key, controller);
        return controller.signal;
    }

    _getHeaders() {
        const headers = {};
        const token = sessionStorage.getItem("accessToken");
        if (token) {
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = 'application/json';
        }
        return headers;
    }

    _addGetParamsToPath(path, data) {
        if (!data || Object.keys(data).length === 0) return path;

        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(data)) {
            const val = value && typeof value === 'object' && value.api_id ? value.api_id : value;
            if (val !== undefined && val !== null) {
                searchParams.set(key, Array.isArray(val) ? val.join(';') : val.toString());
            }
        }
        return `${path}?${searchParams.toString()}`;
    }

    _getFormattedRequestValues(method, path, reqData) {
        let formattedMethod = method;
        let formattedPath = path;
        let responseType = '';
        const additionalHeaders = {};

        if (method === this.REQUEST_DOWNLOAD) {
            formattedMethod = this.REQUEST_GET;
            if (reqData && Object.keys(reqData).length > 0) {
                formattedMethod = this.REQUEST_POST;
            }
            responseType = 'blob';
        }

        if (method === this.REQUEST_GET && reqData) {
            formattedPath = this._addGetParamsToPath(path, reqData);
        }

        if (method === this.REQUEST_FILEUPLOAD) {
            formattedMethod = this.REQUEST_POST;
            additionalHeaders['Content-Type'] = 'multipart/form-data';
        }

        return { formattedMethod, formattedPath, responseType, additionalHeaders };
    }

    getRequestUrl(path) {
        let url = `/api/${path}`;
        if (url.endsWith('/')) url = url.slice(0, -1);
        return url;
    }

    async requestAsync(path, method, reqData = [], key = path, cancelPrevious = false) {
        const signal = this._createSignal(key, cancelPrevious);
        const { formattedMethod, formattedPath, responseType, additionalHeaders } = this._getFormattedRequestValues(method, path, reqData);

        try {
            const response = await axios({
                method: formattedMethod,
                url: this.getRequestUrl(formattedPath),
                data: reqData,
                headers: { ...this._getHeaders(), ...additionalHeaders },
                responseType,
                signal
            });

            if (method === this.REQUEST_DOWNLOAD && response.data) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                let fileName = 'unknown';
                const cd = response.headers['content-disposition'];
                if (cd) {
                    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
                    if (match && match[1]) fileName = match[1].replace(/['"]/g, '');
                }
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
            }

            const isSuccess = response.status >= 200 && response.status < 300 && !response.data?.error;
            return {
                error: !isSuccess,
                canceled: false,
                data: isSuccess ? response.data?.data || null : null,
                pagination: isSuccess ? response.data?.pagination || null : null,
                validationErrors: response.data?.errors || null, // Laravel validation
                messageCodes: response.data?.errorCodes || (isSuccess ? null : ['failed'])
            };

        } catch (error) {
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                return { error: true, canceled: true };
            }

            if (error.response?.status === 401) {
                localStorage.clear();
                sessionStorage.clear();
                router.push('/login');
            }

            return {
                error: true,
                canceled: false,
                data: error.response?.data?.data || null,
                validationErrors: error.response?.data?.errors || null, // Laravel validation
                messageCodes: error.response?.data?.errorCodes || ['failed']
            };
        }
    }
}

export default new ApiRequest();